import React, { useState, useRef, useCallback } from 'react';
import ReactCrop from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Slider } from './ui/slider';
import { RotateCw, ZoomIn, Check, X } from 'lucide-react';

const PRESETS = {
  logo: { label: 'Logo', aspects: [{ label: 'Free', value: undefined }, { label: '1:1', value: 1 }, { label: '16:9', value: 16/9 }, { label: '4:3', value: 4/3 }] },
  qr: { label: 'QR Code', aspects: [{ label: '1:1', value: 1 }] },
  signature: { label: 'Signature', aspects: [{ label: 'Free', value: undefined }, { label: '3:1', value: 3 }, { label: '4:1', value: 4 }, { label: '2:1', value: 2 }] }
};

export const ImageCropDialog = ({ open, onClose, imageSrc, category, onCropComplete }) => {
  const [crop, setCrop] = useState({ unit: '%', width: 80, height: 80, x: 10, y: 10 });
  const [completedCrop, setCompletedCrop] = useState(null);
  const [scale, setScale] = useState(1);
  const [rotate, setRotate] = useState(0);
  const [aspect, setAspect] = useState(category === 'qr' ? 1 : undefined);
  const imgRef = useRef(null);

  const presets = PRESETS[category] || PRESETS.logo;

  const onImageLoad = useCallback((e) => {
    imgRef.current = e.currentTarget;
    const { width, height } = e.currentTarget;
    const cropSize = Math.min(width, height) * 0.8;
    const newCrop = {
      unit: 'px',
      width: aspect ? cropSize : width * 0.8,
      height: aspect ? cropSize / aspect : height * 0.8,
      x: (width - (aspect ? cropSize : width * 0.8)) / 2,
      y: (height - (aspect ? cropSize / aspect : height * 0.8)) / 2,
    };
    setCrop(newCrop);
  }, [aspect]);

  const getCroppedCanvas = useCallback(() => {
    const image = imgRef.current;
    if (!image || !completedCrop) return null;

    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;

    const scaleX = image.naturalWidth / image.width;
    const scaleY = image.naturalHeight / image.height;

    const cropX = completedCrop.x * scaleX;
    const cropY = completedCrop.y * scaleY;
    const cropW = completedCrop.width * scaleX;
    const cropH = completedCrop.height * scaleY;

    canvas.width = cropW;
    canvas.height = cropH;

    ctx.save();
    if (rotate !== 0) {
      ctx.translate(cropW / 2, cropH / 2);
      ctx.rotate((rotate * Math.PI) / 180);
      ctx.translate(-cropW / 2, -cropH / 2);
    }
    if (scale !== 1) {
      ctx.translate(cropW / 2, cropH / 2);
      ctx.scale(scale, scale);
      ctx.translate(-cropW / 2, -cropH / 2);
    }
    ctx.drawImage(image, cropX, cropY, cropW, cropH, 0, 0, cropW, cropH);
    ctx.restore();

    return canvas;
  }, [completedCrop, rotate, scale]);

  const handleConfirm = useCallback(async () => {
    const canvas = getCroppedCanvas();
    if (!canvas) {
      // No crop — use original
      const response = await fetch(imageSrc);
      const blob = await response.blob();
      const file = new File([blob], `${category}.png`, { type: 'image/png' });
      onCropComplete(file);
      onClose();
      return;
    }

    canvas.toBlob((blob) => {
      if (blob) {
        const file = new File([blob], `${category}-cropped.png`, { type: 'image/png' });
        onCropComplete(file);
        onClose();
      }
    }, 'image/png', 0.95);
  }, [getCroppedCanvas, imageSrc, category, onCropComplete, onClose]);

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="w-[95vw] max-w-lg max-h-[90vh] overflow-hidden flex flex-col p-0">
        <DialogHeader className="px-4 pt-4 pb-2">
          <DialogTitle className="text-sm">Crop & Adjust {presets.label}</DialogTitle>
          <DialogDescription className="text-[10px]">Drag to crop. Use controls below to adjust.</DialogDescription>
        </DialogHeader>

        {/* Image Crop Area */}
        <div className="flex-1 overflow-auto px-4 flex items-center justify-center bg-gray-100 dark:bg-gray-900" style={{ minHeight: '200px', maxHeight: '350px' }}>
          {imageSrc && (
            <ReactCrop
              crop={crop}
              onChange={(c) => setCrop(c)}
              onComplete={(c) => setCompletedCrop(c)}
              aspect={aspect}
              className="max-h-[320px]"
            >
              <img
                src={imageSrc}
                alt="Crop preview"
                style={{
                  transform: `scale(${scale}) rotate(${rotate}deg)`,
                  maxHeight: '320px',
                  maxWidth: '100%',
                  objectFit: 'contain',
                }}
                onLoad={onImageLoad}
              />
            </ReactCrop>
          )}
        </div>

        {/* Controls */}
        <div className="px-4 py-3 space-y-3 border-t">
          {/* Aspect Ratio */}
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[10px] text-gray-500 dark:text-muted-foreground w-12">Ratio:</span>
            {presets.aspects.map((a) => (
              <button
                key={a.label}
                onClick={() => setAspect(a.value)}
                className={`px-2 py-0.5 rounded text-[10px] transition-colors ${
                  aspect === a.value
                    ? 'bg-teal-600 text-white'
                    : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-200'
                }`}
              >
                {a.label}
              </button>
            ))}
          </div>

          {/* Zoom */}
          <div className="flex items-center gap-2">
            <ZoomIn className="w-3.5 h-3.5 text-gray-500 flex-shrink-0" />
            <span className="text-[10px] text-gray-500 dark:text-muted-foreground w-10">Zoom</span>
            <Slider
              value={[scale * 100]}
              onValueChange={([v]) => setScale(v / 100)}
              min={50}
              max={200}
              step={5}
              className="flex-1"
            />
            <span className="text-[10px] text-gray-500 w-10 text-right">{Math.round(scale * 100)}%</span>
          </div>

          {/* Rotate */}
          <div className="flex items-center gap-2">
            <RotateCw className="w-3.5 h-3.5 text-gray-500 flex-shrink-0" />
            <span className="text-[10px] text-gray-500 dark:text-muted-foreground w-10">Rotate</span>
            <Slider
              value={[rotate]}
              onValueChange={([v]) => setRotate(v)}
              min={-180}
              max={180}
              step={1}
              className="flex-1"
            />
            <span className="text-[10px] text-gray-500 w-10 text-right">{rotate}°</span>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex items-center justify-end gap-2 px-4 py-3 border-t">
          <Button variant="outline" size="sm" onClick={onClose} className="text-xs">
            <X className="w-3 h-3 mr-1" /> Cancel
          </Button>
          <Button size="sm" onClick={handleConfirm} className="text-xs bg-teal-600 hover:bg-teal-700">
            <Check className="w-3 h-3 mr-1" /> Apply & Upload
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
